import React, { useState } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Send, Mail, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setFormData({ name: '', email: '', phone: '', projectType: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-24 bg-[#050505] relative overflow-hidden" data-testid="contact-section">
      {/* Subtle glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-red-900/10 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-heading tracking-tight uppercase mb-6" data-testid="contact-heading">
            <span className="text-white">Request a </span>
            <span className="text-gradient-noir">Trailer Concept</span>
          </h2>
          <p className="text-base text-gray-500 max-w-3xl mx-auto">
            Send your synopsis and let's create a cinematic trailer for your thriller.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="space-y-6">
            <div className="bg-[#0A0A0A] border border-white/10 p-6 hover:border-red-700/30 transition-all duration-300" data-testid="contact-email-card">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-red-900/30 flex items-center justify-center flex-shrink-0">
                  <Mail className="text-red-500" size={20} />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-1">Email Us</h3>
                  <a 
                    href="mailto:info@uniquevisiondrones.com"
                    className="text-red-500 hover:text-red-400 text-sm transition-colors duration-200"
                    data-testid="contact-email-link"
                  >
                    info@uniquevisiondrones.com
                  </a>
                  <p className="text-gray-600 text-xs mt-1">Get a response within 24 hours</p>
                </div>
              </div>
            </div>

            <div className="bg-[#0A0A0A] border border-white/10 p-6 hover:border-red-700/30 transition-all duration-300" data-testid="contact-consult-card">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-red-900/30 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="text-red-500" size={20} />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-1">Free Consultation</h3>
                  <p className="text-gray-500 text-sm">No commitment, just conversation</p>
                </div>
              </div>
            </div>

            <div className="border border-white/10 p-6" data-testid="contact-expectations">
              <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">What to expect:</h3>
              <ul className="space-y-3 text-sm text-gray-500">
                {['Response within 24 hours', '15-30 min discovery call', 'Custom quote for your project', 'Clear timeline and deliverables'].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <span className="text-red-600 mr-2">-</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2 bg-[#0A0A0A] border border-white/10 p-8" data-testid="contact-form-card">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-wider text-gray-400 mb-2 block">Name *</Label>
                <Input
                  id="name" name="name" type="text" required
                  value={formData.name} onChange={handleChange}
                  placeholder="Your full name"
                  className="bg-transparent border-white/20 text-white placeholder:text-gray-600 focus:border-red-600 focus:ring-1 focus:ring-red-600 rounded-none"
                  data-testid="contact-name-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-gray-400 mb-2 block">Email *</Label>
                  <Input
                    id="email" name="email" type="email" required
                    value={formData.email} onChange={handleChange}
                    placeholder="your@email.com"
                    className="bg-transparent border-white/20 text-white placeholder:text-gray-600 focus:border-red-600 focus:ring-1 focus:ring-red-600 rounded-none"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-gray-400 mb-2 block">Phone</Label>
                  <Input
                    id="phone" name="phone" type="tel"
                    value={formData.phone} onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="bg-transparent border-white/20 text-white placeholder:text-gray-600 focus:border-red-600 focus:ring-1 focus:ring-red-600 rounded-none"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="projectType" className="text-xs uppercase tracking-wider text-gray-400 mb-2 block">Project Type *</Label>
                <Select required value={formData.projectType} onValueChange={(value) => setFormData({ ...formData, projectType: value })}>
                  <SelectTrigger className="bg-transparent border-white/20 text-white focus:border-red-600 rounded-none" data-testid="contact-project-select">
                    <SelectValue placeholder="Select project type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-white/10 rounded-none">
                    <SelectItem value="book" className="text-white hover:bg-white/10">Book Trailer</SelectItem>
                    <SelectItem value="product" className="text-white hover:bg-white/10">Product/Service Promo</SelectItem>
                    <SelectItem value="other" className="text-white hover:bg-white/10">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message" className="text-xs uppercase tracking-wider text-gray-400 mb-2 block">Tell us about your project *</Label>
                <Textarea
                  id="message" name="message" required
                  value={formData.message} onChange={handleChange}
                  placeholder="Share details about your book, target audience, goals, and any specific ideas..."
                  rows={5}
                  className="bg-transparent border-white/20 text-white placeholder:text-gray-600 focus:border-red-600 focus:ring-1 focus:ring-red-600 rounded-none resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-700 hover:bg-red-800 disabled:opacity-50 text-white font-bold uppercase tracking-[0.15em] text-sm py-4 group transition-colors duration-200 flex items-center justify-center"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? 'Sending...' : (
                  <>
                    Send Message
                    <Send className="ml-2 group-hover:translate-x-1 transition-transform duration-200" size={16} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
